import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Select,
  Input,
  Slider,
  Switch,
  Tag,
  Progress,
  Tabs,
  List,
  Table,
} from 'antd';
import {
  ThunderboltOutlined,
  ExperimentOutlined,
  CopyOutlined,
  DownloadOutlined,
  SaveOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const WhiteLabelAITools = () => {
  const [client, setClient] = useState('Acme Co');
  const [model, setModel] = useState('gpt-4o-mini');
  const [tone, setTone] = useState('Professional');
  const [language, setLanguage] = useState('English');
  const [length, setLength] = useState(120);
  const [creativity, setCreativity] = useState(0.6);
  const [includeEmoji, setIncludeEmoji] = useState(false);
  const [cta, setCta] = useState(true);
  const [keywords, setKeywords] = useState('fleet safety, GPS tracking, compliance');
  const [prompt, setPrompt] = useState('Write a LinkedIn ad promoting Pathmarkr white-label analytics for agencies. Focus on ROI, client reporting, and scalability.');

  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const usage = { limit: 50000, used: 13850 }; // mock tokens/credits
  const usagePct = Math.round((usage.used / usage.limit) * 100);

  const clients = ['Acme Co', 'Northwind', 'Contoso', 'AdventureWorks'];
  const models = [
    { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
    { label: 'Llama 3.1 70B', value: 'llama-3.1-70b' },
    { label: 'Claude 3.5 Sonnet', value: 'claude-3.5-sonnet' },
  ];
  const tones = ['Professional', 'Friendly', 'Bold', 'Educational', 'Conversational'];
  const languages = ['English', 'Spanish', 'French', 'German'];

  const templates = [
    { id: 't1', name: 'LinkedIn Sponsored Post', tag: 'Top of Funnel' },
    { id: 't2', name: 'Google RSA Headlines', tag: 'Search' },
    { id: 't3', name: 'Facebook Primary Text', tag: 'Social' },
    { id: 't4', name: 'Email Subject Lines', tag: 'Lifecycle' },
  ];

  const columns = [
    { title: 'Variant', dataIndex: 'variant', key: 'variant', width: 90 },
    { title: 'Preview', dataIndex: 'text', key: 'text' },
    { title: 'Tone', dataIndex: 'tone', key: 'tone', width: 130 },
    { title: 'Score', dataIndex: 'score', key: 'score', width: 90, render: (v) => <Tag color={v >= 85 ? 'green' : v >= 70 ? 'gold' : 'red'}>{v}</Tag> },
    { title: 'Actions', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button size="small" icon={<CopyOutlined />}>Copy</Button>
        <Button size="small" icon={<DownloadOutlined />}>Export</Button>
      </Space>
    )},
  ];

  const generate = async (count = 3) => {
    setLoading(true);
    // Mock latency
    await new Promise(r => setTimeout(r, 600));
    const base = prompt.slice(0, 60);
    const newResults = Array.from({ length: count }).map((_, i) => ({
      key: `${Date.now()}_${i}`,
      variant: `#${i + 1}`,
      tone,
      score: Math.min(95, Math.max(60, Math.round(70 + Math.random() * 30))),
      text: `${base} — ${tone} • ${language}${includeEmoji ? ' 🚀' : ''}${cta ? ' • CTA: Get a demo' : ''}`,
    }));

    setResults(newResults);
    setHistory(prev => [
      {
        id: `${Date.now()}`,
        client,
        model,
        tone,
        language,
        count,
        date: new Date().toLocaleString(),
      },
      ...prev,
    ]);
    setLoading(false);
  };

  const historyColumns = [
    { title: 'When', dataIndex: 'date', key: 'date', width: 180 },
    { title: 'Client', dataIndex: 'client', key: 'client', width: 160 },
    { title: 'Model', dataIndex: 'model', key: 'model', width: 170 },
    { title: 'Tone', dataIndex: 'tone', key: 'tone', width: 140 },
    { title: 'Lang', dataIndex: 'language', key: 'language', width: 120 },
    { title: 'Variants', dataIndex: 'count', key: 'count', width: 110 },
  ];

  const resultList = (
    <Card bodyStyle={{ padding: 0 }}>
      <Table
        rowKey="key"
        columns={columns}
        dataSource={results}
        pagination={{ pageSize: 5 }}
        loading={loading}
      />
    </Card>
  );

  const historyTable = (
    <Card bodyStyle={{ padding: 0 }}>
      <Table rowKey="id" columns={historyColumns} dataSource={history} pagination={{ pageSize: 5 }} />
    </Card>
  );

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Ad Copy AI Tools</Title>
          <Text type="secondary">Generate on-brand copy with controls for tone, length, and creativity. Client-specific limits apply.</Text>
        </div>

        {/* Usage and quick stats */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card>
              <Text type="secondary">Credits Used</Text>
              <Progress percent={usagePct} status={usagePct > 85 ? 'exception' : 'active'} />
              <Text>{usage.used.toLocaleString()} / {usage.limit.toLocaleString()} tokens</Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Space direction="vertical">
                <Text type="secondary">Client</Text>
                <Select value={client} onChange={setClient} style={{ width: '100%' }} options={clients.map(c => ({ value: c, label: c }))} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Space>
                <Button icon={<SaveOutlined />}>Save Template</Button>
                <Button type="primary" icon={<PlusOutlined />}>New Template</Button>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Main composer */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Composer" extra={<Tag icon={<ExperimentOutlined />} color="blue">{model}</Tag>}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <TextArea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={6}
                  placeholder="Describe what you want to generate..."
                />
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">Model</Text>
                      <Select value={model} onChange={setModel} options={models} style={{ width: '100%' }} />
                    </Space>
                  </Col>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">Tone</Text>
                      <Select value={tone} onChange={setTone} options={tones.map(t => ({ value: t, label: t }))} />
                    </Space>
                  </Col>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">Language</Text>
                      <Select value={language} onChange={setLanguage} options={languages.map(l => ({ value: l, label: l }))} />
                    </Space>
                  </Col>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">Length (characters)</Text>
                      <Slider min={60} max={300} value={length} onChange={setLength} tooltip={{ open: false }} />
                    </Space>
                  </Col>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">Creativity</Text>
                      <Slider min={0} max={1} step={0.05} value={creativity} onChange={setCreativity} tooltip={{ open: false }} />
                    </Space>
                  </Col>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text type="secondary">Keywords</Text>
                      <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} />
                    </Space>
                  </Col>
                </Row>
                <Space wrap>
                  <Switch checked={includeEmoji} onChange={setIncludeEmoji} />
                  <Text>Include emoji</Text>
                  <Switch checked={cta} onChange={setCta} />
                  <Text>Add CTA</Text>
                </Space>
                <Space>
                  <Button type="primary" icon={<ThunderboltOutlined />} loading={loading} onClick={() => generate(3)}>Generate</Button>
                  <Button icon={<ThunderboltOutlined />} loading={loading} onClick={() => generate(6)}>More Variations</Button>
                </Space>
              </Space>
            </Card>

            <Tabs
              style={{ marginTop: 16 }}
              items={[
                { key: 'results', label: 'Results', children: resultList },
                { key: 'history', label: 'History', children: historyTable },
              ]}
            />
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Templates">
              <List
                dataSource={templates}
                renderItem={(t) => (
                  <List.Item actions={[<Button type="link" size="small">Use</Button>]}> 
                    <List.Item.Meta title={t.name} description={<Tag>{t.tag}</Tag>} />
                  </List.Item>
                )}
              />
            </Card>
            <Card title="Tips" style={{ marginTop: 16 }}>
              <List
                size="small"
                dataSource={[
                  'Be explicit about target audience and channel.',
                  'Add product benefits and constraints (character limits).',
                  'Use Keywords to guide messaging and SEO.',
                ]}
                renderItem={(item) => <List.Item>- {item}</List.Item>}
              />
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default WhiteLabelAITools;
